import React, { useCallback, useState } from 'react';
import styled from '@emotion/styled';
import { Card, Row, Col } from 'react-bootstrap';
import { SkeletonTable } from '../../../components/Skeleton/Table';

import { useNamespacedTranslation } from '../../../../../hooks/useNamespacedTranslation';
import { ApprovalRequestTableItem } from './mappings';
import { EmptyApprovalRequestsCard } from './EmptyApprovalRequestsCard';
import { TableContentManual } from '../../../manager/components/TableContentManual';
import { CellProps, Column } from 'react-table';
import { ReviewActionsCell } from './ReviewActionsCell';
import { ApprovalRequestStatus } from '../../../__generated__/graphql';
import FormattedDateTime from '../../../../../components/datetime/FormattedDateTime';
import ApprovalRequestStatusCell from './ApprovalRequestStatusCell';
import { ApprovalRequestDetailsSidebar } from './ApprovalRequestDetailsSidebar';
import { EmployeeWithAvatar } from '../../../components/Employee';
import { processRequestName } from './processRequestName';

const PAGE_SIZE = 50;

const i18Path = 'approvalRequest';

type Props = {
  requests: ApprovalRequestTableItem[];
  loading: boolean;
};

export const ApprovalRequestsTable: React.FC<Props> = ({
  requests,
  loading,
}) => {
  const t = useNamespacedTranslation(i18Path);
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(
    null,
  );
  const [showSidebar, setShowSidebar] = useState(false);

  const handleRecordClick = useCallback((request: ApprovalRequestTableItem) => {
    setSelectedRequestId(request.id);
    setShowSidebar(true);
  }, []);

  const handleCloseSidebar = useCallback(() => {
    setShowSidebar(false);
    setSelectedRequestId(null);
  }, []);

  const selectedRequest = selectedRequestId
    ? requests.find((request) => request.id === selectedRequestId)
    : null;

  const reviewCell = useCallback(
    (props: CellProps<ApprovalRequestTableItem>) => {
      if (props.row.original.approvalStatus === ApprovalRequestStatus.Pending) {
        return (
          <ReviewActionsCell
            id={props.row.original.id}
            relatedRecordId={props.row.original.relatedRecordId}
          />
        );
      } else if (props.row.original.actionDatetime) {
        return (
          <Col>
            <FormattedDateTime
              dateTime={props.row.original.actionDatetime}
              format="long"
            />
          </Col>
        );
      } else {
        return <Col></Col>;
      }
    },
    [],
  );

  const columns: Column<ApprovalRequestTableItem>[] = [
    {
      Header: t('table.header.recordType'),
      accessor: 'relatedRecordObjectName',
    },
    {
      Header: t('table.header.recordName'),
      accessor: 'relatedRecordName',
      Cell: (props: CellProps<ApprovalRequestTableItem>) => (
        <Col>
          <a
            href="stub"
            onClick={(e) => {
              e.preventDefault();
              handleRecordClick(props.row.original);
            }}>
            {processRequestName(props.row.original)}
          </a>
        </Col>
      ),
    },
    {
      Header: t('table.header.employee'),
      accessor: 'employee',
      Cell: (props: CellProps<ApprovalRequestTableItem>) => (
        <Col>
          <EmployeeWithAvatar employee={props.row.original.employee} />
        </Col>
      ),
    },
    {
      Header: t('table.header.description'),
      accessor: 'description',
      Cell: (props: CellProps<ApprovalRequestTableItem>) => (
        <Col>{props.row.original.description ?? ''}</Col>
      ),
    },
    {
      Header: t('table.header.approvalStatus'),
      accessor: 'approvalStatus',
      Cell: (props: CellProps<ApprovalRequestTableItem>) => (
        <ApprovalRequestStatusCell status={props.row.original.approvalStatus} />
      ),
    },
    {
      Header: t('table.header.requestedAt'),
      accessor: 'requestedAt',
      Cell: (props: CellProps<ApprovalRequestTableItem>) => (
        <Col>
          <FormattedDateTime
            dateTime={props.row.original.requestedAt}
            format="long"
          />
        </Col>
      ),
    },
    {
      Header: t('table.header.actions'),
      accessor: 'actions',
      Cell: reviewCell,
    },
  ];

  const renderTable = (requests: ApprovalRequestTableItem[]) => {
    if (!requests.length) {
      return <EmptyApprovalRequestsCard />;
    }
    return (
      <TableContentManual
        data={requests}
        columns={columns}
        onRowClick={() => {}}
        pageSize={PAGE_SIZE}
        itemsCount={requests.length}
        initialSortBy={[]}
        onSortByChanged={() => {}}
        initialPageIndex={0}
        onPageIndexChanged={() => {}}
      />
    );
  };

  return (
    <Card>
      <Header className="h-auto">
        <Row className="align-items-center">
          <Col className="small"></Col>
        </Row>
      </Header>

      {requests !== undefined && !loading ? (
        renderTable(requests)
      ) : (
        <SkeletonTable className="card-table" columns={6} rows={8} />
      )}

      {selectedRequest && (
        <ApprovalRequestDetailsSidebar
          show={showSidebar}
          approvalRequestId={selectedRequest.id}
          onClose={handleCloseSidebar}
        />
      )}
    </Card>
  );
};

const Header = styled(Card.Header)({
  height: 'auto',
  minHeight: 60,
});
